import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PD_CONSENT_VERSION, type LegalInfoDto } from '@mafia/core';
import { api } from '@/lib/api';

/** Оператор и контакт берутся с сервера (PD_OPERATOR_NAME / PD_OPERATOR_EMAIL), чтобы не править код при смене. */
function useLegalInfo() {
  const [info, setInfo] = useState<LegalInfoDto | null>(null);
  useEffect(() => {
    api<LegalInfoDto>('/api/legal')
      .then(setInfo)
      .catch(() => setInfo({ operatorName: null, operatorEmail: null, consentVersion: PD_CONSENT_VERSION }));
  }, []);
  return info;
}

function versionDate(version: string) {
  const [y, m, d] = version.split('-');
  return `${d}.${m}.${y}`;
}

function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  const info = useLegalInfo();
  const operator = info?.operatorName ?? '—';
  const email = info?.operatorEmail ?? '—';
  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Link to="/" className="text-sm text-zinc-500 hover:text-white">
        ← На сайт
      </Link>
      <h1 className="mt-4 font-display text-2xl">{title}</h1>
      <p className="mt-1 text-sm text-zinc-500">Редакция от {versionDate(info?.consentVersion ?? PD_CONSENT_VERSION)}</p>
      <div className="legal mt-6 space-y-4 text-sm leading-relaxed text-zinc-300">
        <p>
          Оператор персональных данных: <b>{operator}</b>. Связаться с оператором, отозвать согласие или запросить удаление данных можно по
          адресу <b>{email}</b>.
        </p>
        {children}
      </div>
      <p className="mt-8 flex gap-4 text-sm">
        <Link to="/privacy" className="link">
          Политика обработки персональных данных
        </Link>
        <Link to="/consent" className="link">
          Согласие на обработку
        </Link>
      </p>
    </div>
  );
}

const DATA_LIST = (
  <ul className="list-disc space-y-1 pl-5">
    <li>адрес электронной почты, логин, ник и выбранная аватарка;</li>
    <li>ник в Telegram, если вы его указали в профиле;</li>
    <li>пароль — только в виде необратимого хэша, сам пароль не хранится;</li>
    <li>история и статистика игр: роли, результаты, рейтинг;</li>
    <li>служебные данные входа: даты входов и название браузера.</li>
  </ul>
);

export function ConsentPage() {
  return (
    <LegalShell title="Согласие на обработку персональных данных">
      <p>
        Регистрируясь на сайте «Мафия» и отмечая галочку согласия, я свободно, своей волей и в своём интересе даю оператору согласие на
        обработку моих персональных данных на следующих условиях.
      </p>
      <h2 className="font-display text-lg text-white">Какие данные</h2>
      {DATA_LIST}
      <h2 className="font-display text-lg text-white">Зачем</h2>
      <p>
        Чтобы создать и вести мой аккаунт, впускать меня на сайт, проводить онлайн-игры и считать рейтинг, восстанавливать доступ к
        аккаунту и защищать сайт от нарушителей правил.
      </p>
      <h2 className="font-display text-lg text-white">Что с ними делают</h2>
      <p>
        Сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, блокирование, удаление и уничтожение — с
        использованием средств автоматизации. Ник, аватарка, игровая статистика и ник в Telegram (если указан) видны другим
        зарегистрированным игрокам сайта. Третьим лицам данные не передаются и не продаются.
      </p>
      <h2 className="font-display text-lg text-white">Срок и отзыв</h2>
      <p>
        Согласие действует до удаления аккаунта или до отзыва. Отозвать согласие можно письмом оператору на адрес выше или удалив аккаунт в
        профиле. После отзыва данные удаляются, кроме случаев, когда их хранение требует закон.
      </p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Политика обработки персональных данных">
      <p>
        Политика описывает, какие персональные данные обрабатывает сайт «Мафия», зачем и как они защищены. Она составлена по требованиям
        Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных».
      </p>
      <h2 className="font-display text-lg text-white">Чьи и какие данные</h2>
      <p>Данные зарегистрированных пользователей сайта:</p>
      {DATA_LIST}
      <p>
        Сайт не собирает IP-адреса в базу, не использует сторонние счётчики и рекламные трекеры. Cookie используется одна — служебная, для
        сохранения входа.
      </p>
      <h2 className="font-display text-lg text-white">Цели и основание</h2>
      <p>
        Регистрация и вход, проведение онлайн-игр и подсчёт рейтинга, восстановление доступа, модерация и защита сайта. Основание —
        согласие пользователя (п. 1 ч. 1 ст. 6 Закона № 152-ФЗ), которое даётся при регистрации отдельной галочкой.
      </p>
      <h2 className="font-display text-lg text-white">Как обрабатываются и сколько хранятся</h2>
      <p>
        Обработка автоматизированная. Данные хранятся до удаления аккаунта или отзыва согласия. Записи о входах удаляются через 30 дней
        после окончания сессии, резервные копии базы — через 14 дней. Запись, систематизация, накопление и хранение данных граждан России
        ведутся в базах данных, находящихся на территории Российской Федерации; трансграничная передача не осуществляется.
      </p>
      <h2 className="font-display text-lg text-white">Как защищены</h2>
      <p>
        Пароли хранятся только в виде хэша (argon2id), сайт работает только по HTTPS, доступ к серверам — только по ключам у оператора,
        резервные копии закрыты от посторонних.
      </p>
      <h2 className="font-display text-lg text-white">Ваши права</h2>
      <p>
        Вы можете узнать, какие ваши данные обрабатываются, потребовать их уточнения, блокирования или удаления, отозвать согласие и
        обжаловать действия оператора в Роскомнадзоре или в суде. Запросы направляйте оператору на адрес выше — ответ в течение 10 рабочих
        дней.
      </p>
    </LegalShell>
  );
}
